import React, { useEffect, useState, useMemo } from "react";
import { AccordionComponent } from "../Accordion/Accordion";
import { useTranslation } from "react-i18next";
import { getUrl } from "aws-amplify/storage";
import useAuth from "../../../hooks/useAuth";
import useAnalyticsEventTracker from "../../../hooks/useAnalyticsEventTracker";
import {
  ContentPage,
  ContentPageDocumentFile,
  DocumentFile,
  DocumentFileLanguage,
  LazyContentPageDocumentFile,
  LazyDocumentFile,
} from "../../../models/index.js";
import { DataStore } from "aws-amplify/datastore";

const AccordionWrapper = ({ contentId, content, showSidebar }) => {
  const [pdfData, setPdfData] = useState<{ id: string }[]>([]);
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const gaEventTracker = useAnalyticsEventTracker("Content InfoPanel");
  const [localizedDocumentFiles, setLocalizedDocumentFiles] = useState<LazyContentPageDocumentFile[]>([]);

  useEffect(() => {
    if (!content) return;
    (async () => {
      // Query ContentPageDocumentFiles for ContentPage, if unauthenticated only unprotected DocumentFiles
      const _documentFiles = await DataStore.query(ContentPageDocumentFile, (c) =>
        currentUser
          ? c.contentPage.id.eq(content.id)
          : c.and((c) => [c.contentPage.id.eq(content.id), c.documentFile.protected.eq(false)]),
      );

      // Query DocumentFileLanguage for current language
      const _documentFileLanguage = await DataStore.query(DocumentFileLanguage, (c) => c.language.locale.eq(i18n.language));
      // Filter ContentPageDocumentFiles by connected language so we retrieve only the ones that are in the current language
      const _localizedDocumentFiles = _documentFiles.filter((c) =>
        _documentFileLanguage.some((d) => d.documentFileID === c.documentFileID),
      );
      setLocalizedDocumentFiles(_localizedDocumentFiles);
    })();
  }, [content, currentUser, i18n.language]);

  useEffect(() => {
    localizedDocumentFiles.map(async (contentPageDocumentFile) => {
      try {
        const result = await getUrl({
          key: (await contentPageDocumentFile.documentFile).documentStorageKey,
          options: {
            accessLevel: "guest",
          },
        });

        const { title } = await contentPageDocumentFile.documentFile;

        setPdfData((prevState) => {
          if (prevState.find((pdf) => pdf.id === contentPageDocumentFile.id)) {
            return prevState;
          } else {
            return [
              ...prevState,
              {
                title,
                id: contentPageDocumentFile.id,
                path: result.url.toString(),
                eventTracker: async () => gaEventTracker(`${title} click`),
              },
            ];
          }
        });
      } catch (err) {
        console.log(err);
      }
    });
    return () => setPdfData([]);
  }, [localizedDocumentFiles]);

  const contentArray = useMemo(() => {
    const array: {
      id: number;
      summary: string;
      title: string;
      featuresText?: string;
      benefitsText?: string;
      pdfData?: any;
    }[] = [];

    if (content !== undefined) {
      if (content.featuresTranslationKey !== "") {
        array.push({
          id: 0,
          summary: "features",
          title: t("IP_FEATURES"),
          featuresText: t(content.featuresTranslationKey),
        });
      }
      if (content.benefitsTranslationKey !== "") {
        array.push({
          id: 1,
          summary: "benefits",
          title: t("IP_BENEFITS"),
          benefitsText: t(content.benefitsTranslationKey),
        });
      }
      if (pdfData.length > 0) {
        array.push({
          id: 2,
          summary: "downloads",
          title: t("IP_DOWNLOADS"),
          pdfData,
        });
      }
      return array;
    } else {
      return [];
    }
  }, [content, pdfData]);

  return contentArray.length > 0 && showSidebar ? (
    <AccordionComponent
      iconColor="#3EBA7C"
      summaryBackgroundColor="#000"
      summaryTextColor="#fff"
      detailsBackgroundColor="#fff"
      detailsTextColor="#707070"
      contentArray={contentArray}
      contentId={contentId}
    />
  ) : null;
};

export default AccordionWrapper;
